import React, { useEffect, useState } from 'react';
import { Modal, View } from 'react-native';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import { dispatchService } from '../../../services/central-api/dispatch';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import TimePicker from 'react-time-picker';
import { UnistylesRuntime } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import { minutesToHourFormat, textToMinutes } from '../../../services/time-helpers';
import Toast from 'react-native-toast-message';
import ActingBodiesDropdown from '../../../components/Dropdowns/ActingBodiesDropdown';
import ActingBodiesUnitsDropdown from '../../../components/Dropdowns/ActingBodiesUnitsDropdown';

export default function UnitPauseReasonsModal({ onClose, unitPauseReason, isModalVisible, authenticatedUser }: { onClose: () => void; unitPauseReason?: UnitPauseReason; isModalVisible: boolean; authenticatedUser?: AuthenticatedUser | null; }) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [name, setName] = useState(unitPauseReason?.name || '');
    const [time, setTime] = useState<string>('');
    const [isInvalidTime, setIsInvalidTime] = useState<boolean>(false);
    const [actingBodyId, setActingBodyId] = useState<number>(0);
    const [actingBodyUnitId, setActingBodyUnitId] = useState<number>(0);

    function isNameInvalid(value: string) {
        return value == '';
    }

    function isFormValid() {
        return !isNameInvalid(name);
    }

    function clearValues() {
        setName('');
        setTime('');
        setActingBodyId(0);
        setActingBodyUnitId(0);
    }

    async function loadPageInfo() {
        if (!authenticatedUser) {
            return;
        }
        if (unitPauseReason) {
            setName(unitPauseReason.name);
            setTime(minutesToHourFormat(unitPauseReason.pauseMinutes));
            setActingBodyId(unitPauseReason.actingBodyId);
            setActingBodyUnitId(unitPauseReason.actingBodyUnitId ?? 0);
        } else if (!(authenticatedUser.isAdmin || authenticatedUser.permissions.dispatch_manage_all_acting_bodies_unit_pause_reasons) && authenticatedUser.actingBodyUnit.actingBodyId) {
            setActingBodyId(authenticatedUser.actingBodyUnit.actingBodyId);
        }
    }

    useEffect(() => {
        if (isModalVisible) {
            loadPageInfo();
        }
    }, [isModalVisible]);

    async function formSubmit() {
        try {
            const minutes = textToMinutes(time);
            if (unitPauseReason?.id) {
                await dispatchService.updateUnitPauseReason({
                    id: unitPauseReason.id,
                    name: name,
                    pauseMinutes: minutes,
                    actingBodyUnitId: actingBodyUnitId !== 0 ? actingBodyUnitId : undefined,
                });
            } else {
                await dispatchService.createUnitPauseReason({
                    name: name,
                    pauseMinutes: minutes,
                    actingBodyId: actingBodyId !== 0 ? actingBodyId : undefined,
                    actingBodyUnitId: actingBodyUnitId !== 0 ? actingBodyUnitId : undefined,
                });
            }

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            onClose();
            clearValues();
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return (
        <Modal
            transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                onClose();
                clearValues();
            }}>
            <View style={styles.modalContainer}>
                <View style={styles.centeredView}>
                    <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: '20%' }]}>
                        {(authenticatedUser?.isAdmin ||
                            authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_unit_pause_reasons) &&
                            <ActingBodiesDropdown
                                includeAllOption={false}
                                value={actingBodyId}
                                setValue={setActingBodyId}
                                zIndex={5}
                                disabled={!!unitPauseReason?.id} />
                        }

                        {(authenticatedUser?.isAdmin
                            || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_unit_pause_reasons
                            || authenticatedUser?.permissions.dispatch_manage_acting_body_units_unit_pause_reasons) &&
                            <ActingBodiesUnitsDropdown
                                value={actingBodyUnitId}
                                actingBodyId={actingBodyId}
                                setValue={setActingBodyUnitId}
                                disabled={false}
                                includeEmptyItem={true}
                                zIndex={4} />
                        }
                        <FormInput
                            label={translate('name')}
                            placeholder={translate('name')}
                            value={name}
                            onChangeText={setName}
                            invalid={isNameInvalid}
                        />

                        <View style={{ rowGap: 5 }}>
                            <MyAppText style={styles.label}>{translate('fullPeriod')}</MyAppText>
                            <TimePicker
                                disableClock={true}
                                format='HH:mm'
                                locale='pt-BR'
                                maxDetail='minute'
                                className={['light', 'lightAlt'].includes(UnistylesRuntime.themeName) ? ['inputColor', `inputColorLight`] : ['inputColor', `inputColorDark`]}
                                value={time}
                                clearIcon={null}
                                onInvalidChange={() => setIsInvalidTime(true)}
                                onChange={(value) => {
                                    if (value) {
                                        setIsInvalidTime(false);
                                        setTime(value);
                                    }
                                }}
                            />
                            {isInvalidTime ?
                                <MyAppText style={styles.error}>{translate('invalid_field')}</MyAppText> : null}
                        </View>

                        <FormActions
                            onSubmit={formSubmit}
                            onClose={() => {
                                onClose();
                                clearValues();
                            }}
                            disabled={!isFormValid()} />
                    </View>
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    formContainer: {
        minWidth: 300,
        minHeight: 150,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 25
    },
    modalContainer: {
        flex: 1,
        backgroundColor: '#31313199'
    },
    centeredView: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    label: {
        color: theme.colors.labelColor,
        fontSize: 16
    },
    error: {
        color: '#FF0000',
        fontSize: 10
    },
}));
